/**
 * @file 問題ごとの解説の共通データ (`GET /content/study_works/...`) のクライアント。
 *
 * @what: 本の問題一覧 (StudyWorkIndex) と、問題1つの解説 (StudyProblemGuide) を取る。
 * @why: 家族に属さない配信物なので /content/ 配下にあり、家族 id を持たない。資料 (StudySource) の
 *   work_id で引く。ページは印刷ページで、リーダーの連番への換算は資料の reader_offset で行う。
 * @failure: ApiError を伝播である。404 = 未配信 (呼び出し側が「解説を読み込めませんでした」に倒す)。
 */

import { apiFetch } from "@/shared/api/client";

export interface StudyWorkSection {
  id: string;
  title: string;
  /** 開始ページ (印刷ページ)。 */
  page: number;
}

export type StudyWorkProblemKind = "example" | "check";

export interface StudyWorkProblem {
  id: string;
  kind: StudyWorkProblemKind;
  section_id: string;
  /** 画面に出す名前 ("Theme1-1例題" / "CHECK!4")。 */
  label: string;
  /** 本文で問題が出るページ (印刷ページ)。例題では解法の説明と同じページになる。 */
  page: number;
  /** 問題文だけが載る一覧 (例題一覧など) のページ。解く前に問題だけを読む導線に使う。無ければ省略である。 */
  list_page?: number;
  /** 解答のページ。本文中に解答があるときは省略である。 */
  answer_page?: number;
  source: string;
  summary?: string;
  has_guide: boolean;
}

export interface StudyWorkExtra {
  title: string;
  page: number;
}

export interface StudyWorkIndex {
  work_id: string;
  title: string;
  author?: string;
  publisher?: string;
  isbns?: string[];
  page_count: number;
  sections: StudyWorkSection[];
  problems: StudyWorkProblem[];
  extras?: StudyWorkExtra[];
  version: number;
  content_hash: string;
  published_at: string;
}

export interface StudyGuideCurve {
  kind: "poly" | "named";
  /** 多項式の係数 (定数項から昇順)。 */
  coeffs?: number[];
  /** 名前つき関数 (sin / cos / exp / log / sqrt / inv / tanh。inv は逆数 (1/x))。y = a·f(b·x + c) + d。 */
  name?: string;
  a: number;
  b: number;
  c: number;
  d: number;
  label?: string;
  dashed?: boolean;
}

export interface StudyGuideAreaUnder {
  curve: number;
  from: number;
  to: number;
  label?: string;
}

export interface StudyGuidePolygon {
  points: [number, number][];
  /** -1 = 曲線の標本を足さない。 */
  curve: number;
  curve_from: number;
  curve_to: number;
  label?: string;
}

export interface StudyGuideTick {
  value: number;
  label: string;
}

/** 円 (中心と半径)。表示する側は軸の縮尺に合わせて楕円にする。 */
export interface StudyGuideCircle {
  cx: number;
  cy: number;
  r: number;
  label?: string;
  dashed?: boolean;
}

/** 線分 (x = 定数 の直線や補助線)。 */
export interface StudyGuideSegment {
  from: [number, number];
  to: [number, number];
  label?: string;
  dashed?: boolean;
}

/** 名前つきの点 (接点・交点など)。 */
export interface StudyGuidePoint {
  x: number;
  y: number;
  label?: string;
}

export interface StudyGuidePlot {
  x_range: [number, number];
  y_range: [number, number];
  curves: StudyGuideCurve[];
  area_under?: StudyGuideAreaUnder;
  polygon?: StudyGuidePolygon;
  circles?: StudyGuideCircle[];
  segments?: StudyGuideSegment[];
  points?: StudyGuidePoint[];
  x_ticks?: StudyGuideTick[];
  y_ticks?: StudyGuideTick[];
  caption?: string;
}

/**
 * 解説の本文の1ブロックである。
 *
 * @what: md は本文、plot はグラフ、answer は答案に書く形の模範解答である。kind が md か answer なら text、
 *   plot なら plot を使う。title は answer の折りたたみの見出しで、他の kind では来ない。
 * @why: 方針の説明と模範解答を同じ Markdown で持ちつつ種類で分け、answer だけを折りたたんで出す。
 * @failure: 描く側は kind を見て分岐し、知らない kind は描かない。
 */
export interface StudyGuideBlock {
  kind: "md" | "plot" | "answer";
  /** answer の折りたたみの見出し。無ければ描く側の既定の文言を出す。 */
  title?: string;
  /** 数式つき Markdown ($..$ / $$..$$)。kind が md か answer のとき。 */
  text?: string;
  plot?: StudyGuidePlot;
}

export interface StudyGuideSection {
  id: string;
  heading: string;
  blocks: StudyGuideBlock[];
}

export interface StudyGuideSourceRef {
  title?: string;
  author?: string;
  publisher?: string;
  year?: string;
  pages?: number[];
}

export interface StudyProblemGuide {
  work_id: string;
  problem_id: string;
  derivation_method: "original" | "inspired_by" | "licensed_from";
  source_ref: StudyGuideSourceRef;
  pages?: { label: string; page: number }[];
  sections: StudyGuideSection[];
  version: number;
  content_hash: string;
  published_at: string;
}

export async function fetchStudyWork(
  workId: string,
  signal?: AbortSignal,
): Promise<StudyWorkIndex> {
  return apiFetch<StudyWorkIndex>(
    `/content/study_works/${encodeURIComponent(workId)}`,
    signal ? { signal } : {},
  );
}

export async function fetchStudyProblemGuide(
  workId: string,
  problemId: string,
  signal?: AbortSignal,
): Promise<StudyProblemGuide> {
  return apiFetch<StudyProblemGuide>(
    `/content/study_works/${encodeURIComponent(workId)}/guides/${encodeURIComponent(problemId)}`,
    signal ? { signal } : {},
  );
}
